import { useEffect, useReducer } from 'react';
import { LOGO } from '@/shared/brand';

/**
 * Makes Ghost Cable installable as an app (PWA) and exposes the install state.
 * Importing this module once wires up the manifest, home-screen icons and the service worker.
 */

export const INSTALL_PATH = '/install';

/** The home-screen icon (logo on the dark tile), shown on the install guide. */
export const APP_ICON = LOGO.apple;

// Icons, theme colour and the rest of <head> are written by src/shared/seo.js.
const HEAD_TAGS = [
  { tag: 'link', attrs: { rel: 'manifest', href: '/manifest.json' } },
  { tag: 'meta', attrs: { name: 'mobile-web-app-capable', content: 'yes' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-capable', content: 'yes' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-title', content: 'Ghost Cable' } },
  { tag: 'meta', attrs: { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' } },
];

let deferredPrompt = null;
let justInstalled = false;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn());
}

function addHeadTags() {
  HEAD_TAGS.forEach(({ tag, attrs }) => {
    const key = attrs.rel ? `${tag}[rel="${attrs.rel}"]` : `${tag}[name="${attrs.name}"]`;
    if (document.head.querySelector(key)) return;
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  });
}

function registerWorker() {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
  const go = () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
      // Without the worker the site still works; it just may not offer to install.
    });
  };
  if (document.readyState === 'complete') go();
  else window.addEventListener('load', go, { once: true });
}

function setup() {
  if (typeof window === 'undefined' || window.__gcPwaReady) return;
  window.__gcPwaReady = true;
  addHeadTags();
  registerWorker();
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    justInstalled = true;
    emit();
  });
}

setup();

/** True when the site is already running as the installed app. */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: minimal-ui)').matches ||
    window.navigator.standalone === true
  );
}

/** 'ios' | 'android' | 'mac' | 'desktop', used to open the right set of steps. */
export function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || iPadOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Macintosh/i.test(ua) && /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|Firefox|OPR/i.test(ua)) return 'mac';
  return 'desktop';
}

/** Inside Instagram, Facebook, TikTok and similar, where installing is not possible. */
export function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /FBAN|FBAV|Instagram|Line\/|TikTok|Snapchat|Twitter|LinkedInApp|GSA\//i.test(navigator.userAgent || '');
}

/** Install state for buttons and the install guide. */
export function useInstall() {
  const [, rerender] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    listeners.add(rerender);
    const mq = window.matchMedia?.('(display-mode: standalone)');
    mq?.addEventListener?.('change', rerender);
    return () => {
      listeners.delete(rerender);
      mq?.removeEventListener?.('change', rerender);
    };
  }, []);

  async function prompt() {
    const e = deferredPrompt;
    if (!e) return 'unavailable';
    e.prompt();
    let outcome = 'dismissed';
    try {
      outcome = (await e.userChoice)?.outcome ?? 'dismissed';
    } finally {
      deferredPrompt = null;
      emit();
    }
    return outcome;
  }

  return {
    canPrompt: !!deferredPrompt,
    installed: justInstalled || isStandalone(),
    platform: detectPlatform(),
    inAppBrowser: isInAppBrowser(),
    prompt,
  };
}
